import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SelectOption, useDebounce, useInfiniteScroll } from '@innowise-group/core';
import { Select, SelectProps } from '@innowise-group/mui-kit';

export interface InfiniteScrollSelectLoadParams {
  page: number;
  searchValue: string;
}

export interface ExternalStateInfiniteScrollSelectProps
  extends Omit<SelectProps, 'lastElementRef' | 'loading' | 'loadingAdditionalOptions' | 'options'> {
  options: SelectProps['options'];
  isLoading: boolean;
  totalPages?: number;
  selectedOption?: SelectOption;
  initialOptionsLoaded?: boolean;
  initialPage?: number;
  debounceDelay?: number;
  loadOptions: (params: InfiniteScrollSelectLoadParams) => unknown;
  resetOptions?: () => void;
}

const ExternalStateInfiniteScrollSelect: React.FC<ExternalStateInfiniteScrollSelectProps> = ({
  options,
  isLoading,
  totalPages,
  selectedOption,
  initialOptionsLoaded = false,
  initialPage = 1,
  debounceDelay,
  loadOptions,
  resetOptions,
  onInputChange,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue] = useDebounce(searchValue, debounceDelay);
  const searchValueRef = useRef('');
  const isInitialSearch = useRef(true);
  const initialOptionsLoadedOnMount = useRef(initialOptionsLoaded);
  const loadOptionsRef = useRef(loadOptions);
  const resetOptionsRef = useRef(resetOptions);
  const { lastElementRef, pageNum, resetPageNum } = useInfiniteScroll(isLoading, totalPages);

  const selectOptions = useMemo(() => {
    if (props.multiple || !selectedOption || selectedOption.value !== props.value) return options;

    return [selectedOption, ...options.filter((option) => option.value !== selectedOption.value)];
  }, [options, props.multiple, props.value, selectedOption]);

  loadOptionsRef.current = loadOptions;
  resetOptionsRef.current = resetOptions;

  useEffect(() => {
    if (!initialOptionsLoadedOnMount.current) {
      loadOptionsRef.current({ page: initialPage, searchValue: '' });
    }

    return () => resetOptionsRef.current?.();
  }, [initialPage]);

  useEffect(() => {
    if (isInitialSearch.current) {
      isInitialSearch.current = false;
      return;
    }

    const normalizedSearchValue = debouncedSearchValue.trim();
    searchValueRef.current = normalizedSearchValue;
    resetPageNum();
    loadOptionsRef.current({ page: initialPage, searchValue: normalizedSearchValue });
  }, [debouncedSearchValue, initialPage, resetPageNum]);

  useEffect(() => {
    if (pageNum > 1) {
      loadOptionsRef.current({
        page: initialPage + pageNum - 1,
        searchValue: searchValueRef.current,
      });
    }
  }, [initialPage, pageNum]);

  const handleInputChange: SelectProps['onInputChange'] = (event, inputValue, reason) => {
    onInputChange?.(event, inputValue, reason);

    if (reason === 'input' || reason === 'clear') {
      setSearchValue(inputValue);
    }
  };

  return (
    <Select
      {...props}
      options={selectOptions}
      loading={isLoading}
      loadingAdditionalOptions={isLoading && pageNum > 1}
      lastElementRef={lastElementRef}
      onInputChange={handleInputChange}
    />
  );
};

export default ExternalStateInfiniteScrollSelect;
